import { EQUAL_WEIGHTS_TITLE } from '../../data';
import { pickDefaultModelResult } from '../../data-point-pickers';
import { isImpactCategoryVisible } from '../../index';
import { applyValueSetToImpactScoresByIVV } from '../../input/value-sets';
import { ValueSet } from '../../input/value-sets/types';
import { Valence } from '../../types';
import { forEachImpactScoreByIVV } from './index';
import { ModelEntityWithScores, ModelResultWithScores, SpecialModelResultId } from './model-entity-data/types';
import { FinalImpactScoreType, ImpactScoresByIVV, NetScoreFormat } from './types';

export const NEGATIVE_INFINITE_NET_IMPACT_RATIO = -999999;

/**
 * With NetScoreFormat, you can override the default formats that are decided based on scoreType.
 */
export function computeNetScore(
  impactScoresByIVV: ImpactScoresByIVV,
  scoreType: FinalImpactScoreType,
  formatOverride?: NetScoreFormat,
) {
  const netScoreFormat = getNetScoreFormat(scoreType, formatOverride);

  switch (netScoreFormat) {
    case NetScoreFormat.NET_SUM:
      return computeNetImpactSum(impactScoresByIVV);
    default:
      return computeNetImpactRatio(impactScoresByIVV);
  }
}

function getNetScoreFormat(scoreType: FinalImpactScoreType, formatOverride?: NetScoreFormat) {
  if (formatOverride) {
    return formatOverride;
  }
  switch (scoreType) {
    case FinalImpactScoreType.RELATIVE:
      return NetScoreFormat.NET_RATIO;
    case FinalImpactScoreType.ABSOLUTE:
      return NetScoreFormat.NET_SUM;
    case FinalImpactScoreType.CENTS_PER_DOLLAR:
      return NetScoreFormat.NET_RATIO;
    case FinalImpactScoreType.DOLLARS_PER_YEAR:
      return NetScoreFormat.NET_SUM;
  }
}

export function computeNetImpactRatio(data: ImpactScoresByIVV) {
  const positive = computeNetImpactSum(data, Valence.POSITIVE);
  const negative = -computeNetImpactSum(data, Valence.NEGATIVE);
  return positive ? (positive - negative) / positive : NEGATIVE_INFINITE_NET_IMPACT_RATIO; // special case -Infinity
}

export function computeNetImpactRatioWithNegativeValenceDivider(data: ImpactScoresByIVV) {
  const positive = computeNetImpactSum(data, Valence.POSITIVE);
  const negative = -computeNetImpactSum(data, Valence.NEGATIVE);
  return negative ? (positive - negative) / negative : 9999999; // special case Infinity
}

export function computeNetImpactSum(impactScoresByIVV: ImpactScoresByIVV, valence?: Valence): number {
  let ret = 0;

  const includePositive = valence === undefined || valence === Valence.POSITIVE;
  const includeNegative = valence === undefined || valence === Valence.NEGATIVE;

  forEachImpactScoreByIVV(impactScoresByIVV, score => {
    if (!isImpactCategoryVisible(score.impactCode)) {
      return;
    }
    /**
     * Temp fix to handle missing values in the database - this should be properly fixed
     * with https://github.com/uprightproject/upright/pull/1078
     */
    if (score.valence === Valence.POSITIVE && includePositive) {
      ret += score.value || 0;
    } else if (score.valence === Valence.NEGATIVE && includeNegative) {
      ret -= score.value || 0;
    }
  });
  return ret;
}

export function getDefaultNetScoreOfEntity(
  modelEntity: ModelEntityWithScores,
  scoreType: FinalImpactScoreType.ABSOLUTE | FinalImpactScoreType.RELATIVE,
  valueSet: ValueSet,
  valueSetName: string,
): number {
  const modelResults = modelEntity.modelResults;
  if (modelResults.length > 1) {
    throw Error(
      `Not allowed to have multiple model results per weight set item. Entity: ${modelEntity.type}, ${modelEntity.id}`,
    );
  }
  return getNetScoreOfResult(pickDefaultModelResult(modelResults), scoreType, valueSet, valueSetName);
}

export function getNetScoreOfResult(
  modelResult: Pick<ModelResultWithScores, 'resultId' | 'netScoresByType' | 'impactScoresByTIVV'>,
  scoreType: FinalImpactScoreType.ABSOLUTE | FinalImpactScoreType.RELATIVE,
  valueSet: ValueSet,
  valueSetName: string,
): number {
  if (modelResult.resultId === SpecialModelResultId.NO_SCORES) {
    return NaN;
  }
  if (valueSetName === EQUAL_WEIGHTS_TITLE) {
    const precomputedScore = modelResult.netScoresByType[scoreType];
    if (precomputedScore !== undefined) {
      return precomputedScore;
    }
  }
  // Compute in UI then
  const scores = modelResult.impactScoresByTIVV[scoreType];

  if (!scores) {
    return NaN;
  }
  const scoresWithValueSet = applyValueSetToImpactScoresByIVV(scores, valueSet);
  const netScore = computeNetScore(scoresWithValueSet, scoreType);
  return netScore;
}
